import React, { useState } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { contato } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MessageCircle, Send } from 'lucide-react';

const MAX_MENSAGEM = 5000;

function ContatoPage() {
  const { user } = useAuth();
  const [assunto, setAssunto] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);

  const nome = user?.nome || 'Usuário';
  const email = user?.email || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviando) return;

    const assuntoLimpo = assunto.trim();
    const mensagemLimpa = mensagem.trim();

    if (!assuntoLimpo || !mensagemLimpa) {
      toast.error('Preencha o assunto e a mensagem.');
      return;
    }

    setEnviando(true);
    try {
      await contato.enviar({
        assunto: assuntoLimpo,
        mensagem: mensagemLimpa,
      });
      toast.success('Mensagem enviada! Vamos responder no seu e-mail em breve.');
      setAssunto('');
      setMensagem('');
    } catch (error) {
      console.error('Failed to send contact message:', error);
      toast.error('Não foi possível enviar sua mensagem. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Layout showMonthSelector={false}>
      <div className="space-y-6 animate-fade-in" data-testid="contato-page">
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-[#1B2A4A] flex items-center gap-3">
              <MessageCircle size={24} />
              Fale conosco
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-base text-slate-600 mb-6">
              Tem uma dúvida, sugestão ou encontrou um problema? Envie sua mensagem
              e nossa equipe responde no seu e-mail em português.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nome e e-mail (da conta, somente leitura) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-base font-semibold text-[#1B2A4A]">Seu nome</Label>
                  <Input
                    value={nome}
                    disabled
                    className="h-12 text-base bg-slate-50 border-2 border-slate-200 text-slate-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-semibold text-[#1B2A4A]">Seu e-mail</Label>
                  <Input
                    value={email}
                    disabled
                    className="h-12 text-base bg-slate-50 border-2 border-slate-200 text-slate-500"
                  />
                </div>
              </div>
              <p className="text-sm text-slate-500 -mt-2">
                Vamos responder para este e-mail.
              </p>

              {/* Assunto */}
              <div className="space-y-2">
                <Label htmlFor="assunto" className="text-base font-semibold text-[#1B2A4A]">
                  Assunto
                </Label>
                <Input
                  id="assunto"
                  value={assunto}
                  onChange={(e) => setAssunto(e.target.value)}
                  maxLength={150}
                  placeholder="Ex.: Dúvida sobre meu plano"
                  className="h-12 text-base border-2 border-slate-200 focus-visible:border-[#1B2A4A]"
                  data-testid="contato-assunto"
                />
              </div>

              {/* Mensagem */}
              <div className="space-y-2">
                <Label htmlFor="mensagem" className="text-base font-semibold text-[#1B2A4A]">
                  Mensagem
                </Label>
                <Textarea
                  id="mensagem"
                  value={mensagem}
                  onChange={(e) => setMensagem(e.target.value.slice(0, MAX_MENSAGEM))}
                  rows={6}
                  placeholder="Escreva sua mensagem com o máximo de detalhes possível."
                  className="text-base border-2 border-slate-200 focus-visible:border-[#1B2A4A] resize-none"
                  data-testid="contato-mensagem"
                />
                <p className="text-sm text-slate-400 text-right">
                  {mensagem.length}/{MAX_MENSAGEM}
                </p>
              </div>

              <Button
                type="submit"
                disabled={enviando}
                className="h-12 px-6 text-base font-semibold bg-[#1B2A4A] hover:bg-[#152137] text-white rounded-xl w-full sm:w-auto"
                data-testid="contato-enviar-btn"
              >
                <Send size={20} className="mr-2" />
                {enviando ? 'Enviando...' : 'Enviar mensagem'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default ContatoPage;
